import { useRouter } from 'next/router';

import { useOAIInfiniteQuery, useOAIQuery } from '~/hooks/reactQuery';
import type { OAIParameters, OAISchema } from '~/types/openapi';

export const useECouponBrandInfo = ({ brandId }: { brandId: number }) => {
  const query = useOAIQuery({
    queryKey: '/api/market-place/v1/e-coupon/{brandId}',
    variables: { brandId },
    queryOptions: {
      enabled: !!Number(brandId),
    },
  });

  return query;
};

type ProductTotalCountOptions = {
  enabled?: boolean;
  variables?: OAIParameters<'/api/market-place/v3/products/count', 'get'>;
};

const useProductTotalCount = (options?: ProductTotalCountOptions) => {
  const { data: totalCount } = useOAIQuery({
    queryKey: '/api/market-place/v3/products/count',
    variables: options?.variables,
    queryOptions: {
      ...options,
      suspense: false,
      keepPreviousData: true,
    },
  });

  return totalCount;
};

export const useMarketSelectECouponProducts = ({
  brandId,
}: {
  brandId: number;
}) => {
  const router = useRouter();
  const variables: Record<string, unknown> = {
    ...router.query,
    eCouponBrandId: brandId,
  };

  const totalCount = useProductTotalCount({
    variables: variables as OAIParameters<
      '/api/market-place/v3/products/count',
      'get'
    >,
  });
  const infiniteQuery = useOAIInfiniteQuery<
    '/api/market-place/v3/products',
    OAISchema['MarketSelectProductResponseV2']
  >({
    queryKey: '/api/market-place/v3/products',
    variables,
    queryOptions: {
      keepPreviousData: true,
      suspense: true,
      useErrorBoundary: true,
      getNextPageParam: ({ hasNextPage, lastId, searchAfter }) => {
        if (!hasNextPage) {
          return undefined;
        }

        return { lastId, searchAfter };
      },
      enabled: !!variables.brandId && !Number.isNaN(brandId),
    },
  });

  return {
    total: totalCount || 0,
    ...infiniteQuery,
  };
};
